/**
 * TRANSLATIONS
 * ─────────────────────────────────────────────────────────────────────────────
 * All user-facing strings in English and Turkish.
 * Admin edits these via the CMS rich-text editor per language.
 *
 * Firestore collection: `translations`
 * Documents: `en`, `tr`
 */

export const TRANSLATIONS = {
  en: {
    nav: {
      home:    'Home',
      about:   'About Us',
      privacy: 'Privacy Policy',
      terms:   'Terms & Conditions',
      contact: 'Contact Us',
      signIn:  'Sign In',
      signUp:  'Sign Up',
    },

    hero: {
      slides: [
        {
          title:    'Trade What You Love.',
          subtitle: 'Swap products with people near you — no cash needed. Swipe, match, and trade in seconds.',
          cta:      'Start Trading',
          ctaHref:  '#features',
        },
        {
          title:    'Smart. Simple. Secure.',
          subtitle: 'Bitakas puts peer-to-peer trading in your pocket. Escrow-protected, community-driven.',
          cta:      'Learn More',
          ctaHref:  '#how-it-works',
        },
        {
          title:    'List in One Step.',
          subtitle: 'Upload a photo, add a description, and your listing is live. It takes seconds.',
          cta:      'Get Started',
          ctaHref:  '/signup',
        },
      ],
    },

    categories: {
      label:      'Categories',
      heading:    'Browse by Category',
      subheading: "Find exactly what you're looking for across dozens of categories.",
      names: {
        electronics: 'Electronics',
        phone:       'Phones',
        fashion:     'Fashion',
        sports:      'Sports & Outdoor',
        home:        'Home & Living',
        auto:        'Vehicles',
        gaming:      'Gaming',
        baby:        'Baby & Toys',
        pets:        'Pets',
        tools:       'Tools & Garden',
        books:       'Books & Hobby',
        watches:     'Watches',
        jewelry:     'Jewelry',
        music:       'Music',
        camera:      'Cameras',
      },
    },

    features: {
      label:      'Features',
      heading:    'Why Traders Choose Bitakas',
      subheading: 'Everything you need for safe, simple peer-to-peer trading — all in one app.',
      items: [
        {
          title:       'Swipe to Trade',
          description: "Browse listings Tinder-style. Swipe right to save, left to skip. Discover items you'll love effortlessly.",
        },
        {
          title:       'No Sign-Up to Browse',
          description: "Start exploring anonymously. Your swipes stay on your device. Create an account only when you're ready.",
        },
        {
          title:       'Direct Messaging',
          description: 'Message any seller instantly — no matching required. Propose your trade offer directly.',
        },
        {
          title:       'Secure Escrow',
          description: 'When cash is part of a trade, Iyzico holds it safely until both parties confirm.',
        },
        {
          title:       'Promoted Listings',
          description: 'Upgrade to Pro and get your listings promoted to the top of the feed for faster trades.',
        },
        {
          title:       'Full Trade History',
          description: 'Track every swap with a unique Order ID. Full transparency, complete records.',
        },
      ],
    },

    howItWorks: {
      label:   'How It Works',
      heading: 'Trading Made Simple',
      steps: [
        {
          title:       'Browse & Swipe',
          description: 'Open the app and start swiping through product listings. No account needed to explore.',
        },
        {
          title:       'List Your Item',
          description: 'One photo, one description — your listing is live in seconds. Select a category and region.',
        },
        {
          title:       'Message & Negotiate',
          description: 'Found something you want? Message the seller directly and propose your trade.',
        },
        {
          title:       'Trade Securely',
          description: 'Confirm the trade. If cash is involved, escrow protects both sides until everyone agrees.',
        },
      ],
    },

    listings: {
      label:      'Marketplace',
      heading:    "See What's Trending",
      subheading: 'Real listings from real traders. Discover what\'s hot on Bitakas right now.',
    },

    pricing: {
      label:      'Pricing',
      heading:    'Simple, Transparent Pricing',
      trialNote:  'Start with a 2-week free trial — no credit card required.',
      getStarted: 'Get Started',
      perMonth:   '/month',
      plans: [
        {
          name:        'Regular',
          description: 'Everything you need to trade',
          features: [
            'Unlimited browsing & swiping',
            'List up to 10 items',
            'Direct messaging',
            'Escrow-protected trades',
            'Full trade history',
          ],
        },
        {
          name:        'Pro',
          badge:       'Most Popular',
          description: 'For power traders',
          features: [
            'Everything in Regular',
            'Unlimited listings',
            'Promoted listings in feed',
            'Priority support',
            'Early access to features',
          ],
        },
      ],
    },

    faq: {
      label:   'FAQ',
      heading: 'Frequently Asked Questions',
      items: [
        {
          q: 'Do I need an account to browse?',
          a: 'No. You can swipe and explore listings without signing up. You only need an account to message a seller or list your own product.',
        },
        {
          q: 'How does the free trial work?',
          a: 'When you create a Bitakas account, you get 2 weeks of full access for free — no credit card required. After 14 days, choose a plan to continue.',
        },
        {
          q: 'What happens when a trade includes cash?',
          a: 'Any cash in a trade is held securely through our escrow system powered by Iyzico. Money is released only when both users confirm.',
        },
        {
          q: 'Can I cancel my subscription?',
          a: 'Yes. Cancel anytime from your app store settings or your Bitakas account. Access continues until the end of the billing period.',
        },
        {
          q: 'What if I have a dispute?',
          a: 'Contact our support team. Our admin reviews trade messages and makes a fair decision. Escrow funds can be released or refunded manually.',
        },
        {
          q: 'Is my payment information secure?',
          a: 'All payments go through Iyzico (website) and Apple/Google Pay (in-app), which are PCI-compliant. Bitakas never stores card details.',
        },
      ],
    },

    download: {
      heading:         'Download Bitakas',
      subheading:      'Available on iOS and Android. Start trading in minutes.',
      comingSoon:      'Coming Soon',
      comingSoonNote:  'We\'re putting the finishing touches on the app. Sign up to get notified when we launch.',
      appStore:        'App Store',
      playStore:       'Google Play',
    },

    footer: {
      tagline:  'The smarter way to trade.',
      platform: 'Platform',
      legal:    'Legal',
      company:  'Company',
    },

    // Static pages (About, Terms, Privacy, Contact) placeholder copy
    about: {
      heading: 'About Bitakas',
      mission: {
        heading: 'Our Mission',
        body:    'At Bitakas, we believe trading should be simple, safe, and community-driven. We built a platform where you can swap the things you love — without complicated listings, fees, or waiting around.',
      },
      story: {
        heading: 'Our Story',
        body:    'Bitakas started with a simple idea: why buy new when you can trade? We built a platform that makes peer-to-peer swapping as intuitive as swiping through your favorite app.',
      },
    },

    contact: {
      heading:       'Get in Touch',
      subheading:    'We respond within 24 hours.',
      namePlaceholder:    'Your Name',
      emailPlaceholder:   'Your Email',
      subjectPlaceholder: 'Subject',
      messagePlaceholder: 'Your message...',
      submitButton:  'Send Message',
      successMsg:    'Message sent! We\'ll get back to you shortly.',
    },
  },

  // ── Turkish ────────────────────────────────────────────────────────────────
  tr: {
    nav: {
      home:    'Anasayfa',
      about:   'Hakkımızda',
      privacy: 'Gizlilik Politikası',
      terms:   'Kullanım Koşulları',
      contact: 'İletişim',
      signIn:  'Giriş Yap',
      signUp:  'Kayıt Ol',
    },

    hero: {
      slides: [
        {
          title:    'Sevdiğin Şeyleri Takasla.',
          subtitle: 'Yakınındaki insanlarla ürün takası yap — nakit gerekmez. Kaydır, eşleş ve hemen takasla.',
          cta:      'Takasa Başla',
          ctaHref:  '#features',
        },
        {
          title:    'Akıllı. Basit. Güvenli.',
          subtitle: 'Bitakas ile eşler arası takas cebinde. Emanet korumalı, topluluk odaklı.',
          cta:      'Daha Fazla',
          ctaHref:  '#how-it-works',
        },
        {
          title:    'Tek Adımda İlan Ver.',
          subtitle: 'Bir fotoğraf yükle, açıklama ekle ve ilanın anında yayında.',
          cta:      'Hemen Başla',
          ctaHref:  '/signup',
        },
      ],
    },

    categories: {
      label:      'Kategoriler',
      heading:    'Kategoriye Göre Ara',
      subheading: 'Onlarca kategoride aradığınızı kolayca bulun.',
      names: {
        electronics: 'Elektronik',
        phone:       'Telefon',
        fashion:     'Giyim & Aksesuar',
        sports:      'Spor & Outdoor',
        home:        'Ev & Yaşam',
        auto:        'Araçlar',
        gaming:      'Oyun',
        baby:        'Anne & Bebek',
        pets:        'Pet Shop',
        tools:       'Yapı Market & Bahçe',
        books:       'Hobi & Kitap',
        watches:     'Saat',
        jewelry:     'Takı & Aksesuar',
        music:       'Müzik',
        camera:      'Kamera',
      },
    },

    features: {
      label:      'Özellikler',
      heading:    'Neden Bitakas?',
      subheading: 'Güvenli ve basit eşler arası takas için ihtiyacınız olan her şey.',
      items: [
        { title: 'Kaydırarak Takasla', description: 'İlanları Tinder tarzı incele. Sağa kaydır kaydet, sola kaydır geç.' },
        { title: 'Kayıt Olmadan Göz At', description: 'Anonim olarak keşfet. Kaydırma verilerin cihazında kalır.' },
        { title: 'Doğrudan Mesajlaşma', description: 'Herhangi bir satıcıya anında mesaj at — eşleşme gerekmez.' },
        { title: 'Güvenli Emanet', description: 'Nakit takas parçasıysa, Iyzico her iki taraf onaylayana kadar güvenle tutar.' },
        { title: 'Öne Çıkan İlanlar', description: "Pro'ya yükselt ve ilanların akışın en üstünde görünsün." },
        { title: 'Tam Takas Geçmişi', description: 'Her takası benzersiz Sipariş ID ile takip et. Tam şeffaflık.' },
      ],
    },

    howItWorks: {
      label:   'Nasıl Çalışır',
      heading: 'Takas Çok Kolay',
      steps: [
        { title: 'Göz At & Kaydır', description: 'Uygulamayı aç ve ilanları kaydırmaya başla. Hesap gerekmez.' },
        { title: 'İlanını Paylaş', description: 'Bir fotoğraf, bir açıklama — ilanın saniyeler içinde yayında.' },
        { title: 'Mesajlaş & Pazarlık Et', description: 'Bir şey buldun mu? Satıcıya mesaj at ve takasını öner.' },
        { title: 'Güvenle Takasla', description: 'Takası onayla. Nakit varsa emanet sistemi her iki tarafı korur.' },
      ],
    },

    listings: {
      label:      'Pazar Yeri',
      heading:    'Trendlere Göz At',
      subheading: "Bitakas'taki gerçek kullanıcılardan gerçek ilanlar.",
    },

    pricing: {
      label:      'Fiyatlandırma',
      heading:    'Basit, Şeffaf Fiyatlandırma',
      trialNote:  '2 hafta ücretsiz deneyin — kredi kartı gerekmez.',
      getStarted: 'Başla',
      perMonth:   '/ay',
      plans: [
        {
          name:        'Normal',
          description: 'Takas için ihtiyacınız olan her şey',
          features: [
            'Sınırsız gezinme ve kaydırma',
            '10 adede kadar ilan',
            'Doğrudan mesajlaşma',
            'Emanet korumalı takaslar',
            'Tam takas geçmişi',
          ],
        },
        {
          name:        'Pro',
          badge:       'En Popüler',
          description: 'Güçlü takasçılar için',
          features: [
            "Normal'deki her şey",
            'Sınırsız ilan',
            'Akışta öne çıkan ilanlar',
            'Öncelikli destek',
            'Yeni özelliklere erken erişim',
          ],
        },
      ],
    },

    faq: {
      label:   'SSS',
      heading: 'Sıkça Sorulan Sorular',
      items: [
        { q: 'Göz atmak için hesap gerekli mi?', a: 'Hayır. Kayıt olmadan ilanları kaydırabilir ve keşfedebilirsiniz.' },
        { q: 'Ücretsiz deneme nasıl çalışır?', a: 'Hesap oluşturduğunuzda 2 hafta ücretsiz tam erişim — kredi kartı gerekmez.' },
        { q: 'Takas nakit içerirse ne olur?', a: 'Takastaki nakit Iyzico ile güvenle tutulur. Her iki kullanıcı onaylayana kadar serbest bırakılmaz.' },
        { q: 'Aboneliğimi iptal edebilir miyim?', a: 'Evet. İstediğiniz zaman uygulama mağazasından veya hesabınızdan iptal edebilirsiniz.' },
        { q: 'Anlaşmazlık yaşarsam ne olur?', a: 'Destek ekibimiz mesajları inceler ve adil karar verir.' },
        { q: 'Ödeme bilgilerim güvende mi?', a: 'Tüm ödemeler PCI uyumlu Iyzico ve Apple/Google Pay üzerinden işlenir.' },
      ],
    },

    download: {
      heading:        "Bitakas'ı İndir",
      subheading:     'iOS ve Android için mevcut. Dakikalar içinde takasa başla.',
      comingSoon:     'Çok Yakında',
      comingSoonNote: 'Uygulamaya son dokunuşları yapıyoruz. Lansman bildirimi almak için kaydolun.',
      appStore:       'App Store',
      playStore:      'Google Play',
    },

    footer: {
      tagline:  'Takasın akıllı yolu.',
      platform: 'Platform',
      legal:    'Yasal',
      company:  'Şirket',
    },

    about: {
      heading: 'Hakkımızda',
      mission: {
        heading: 'Misyonumuz',
        body:    'Bitakas olarak takasın basit, güvenli ve topluluk odaklı olması gerektiğine inanıyoruz.',
      },
      story: {
        heading: 'Hikayemiz',
        body:    'Bitakas basit bir fikirle başladı: yeni almak yerine neden takas etmeyesiniz?',
      },
    },

    contact: {
      heading:            'İletişime Geçin',
      subheading:         '24 saat içinde yanıt veriyoruz.',
      namePlaceholder:    'Adınız',
      emailPlaceholder:   'E-posta Adresiniz',
      subjectPlaceholder: 'Konu',
      messagePlaceholder: 'Mesajınız...',
      submitButton:       'Mesaj Gönder',
      successMsg:         'Mesajınız iletildi! En kısa sürede dönüş yapacağız.',
    },
  },
};
